/**
 * Role Define Component
 * 
 * Tab component for managing dynamic roles in the User Role Management page
 * Allows superusers to create, edit, and delete custom roles
 * 
 * Features:
 * - List all roles with user count
 * - Create new custom roles
 * - Edit role properties (label, description, color, permissions)
 * - Delete custom roles (not system roles)
 * - Color picker with predefined Tailwind CSS classes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { services } from '../../../services/api';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { Loader } from '../../../components/ds';

// Predefined color options for role badges
const COLOR_OPTIONS = [
  { name: 'Red', value: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  { name: 'Green', value: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  { name: 'Blue', value: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { name: 'Yellow', value: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  { name: 'Purple', value: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  { name: 'Pink', value: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200' },
  { name: 'Indigo', value: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  { name: 'Orange', value: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  { name: 'Teal', value: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200' },
  { name: 'Gray', value: 'bg-bg-3 text-fg-1 dark:bg-bg-3 dark:text-gray-200' },
];

// Available permissions
const AVAILABLE_PERMISSIONS = [
  { id: 'admin:all', label: 'Full Admin Access', description: 'Complete access to all features' },
  { id: 'read:all', label: 'Read All', description: 'Read access to all resources' },
  { id: 'write:all', label: 'Write All', description: 'Write access to all resources' },
  { id: 'read:trades', label: 'Read Trades', description: 'View trading data' },
  { id: 'write:trades', label: 'Write Trades', description: 'Execute trades' },
  { id: 'read:market_data', label: 'Read Market Data', description: 'View market information' },
  { id: 'read:analytics', label: 'Read Analytics', description: 'View analytics dashboards' },
  { id: 'read:reports', label: 'Read Reports', description: 'View reports' },
  { id: 'read:basic', label: 'Basic Read', description: 'Basic viewing permissions' },
];

const RoleDefine = () => {
  const { showSnackbar } = useSnackbar();
  //! Phone gets cards, not the table — same reason as the user list: a 5-column
  //! table only "fits" 375px by scrolling sideways, which reads as cut off, and
  //! here it buried the Edit/Delete actions in the last column so you'd have to
  //! pan past four columns to reach them.
  const { isPhone } = useBreakpoint();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    color: COLOR_OPTIONS[9].value, // Default to gray
    permissions: [],
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  // Helper function to get more specific error messages
  const getErrorMessage = (error, defaultMessage) => {
    const status = error.response?.status || error.status;
    if (status === 403) {
      return 'Access denied. Please ensure you have superuser privileges.';
    }
    if (status === 404) {
      return 'Role management endpoint not available.';
    }
    if (status === 400) {
      return error.response?.data?.detail || 'Invalid role data.';
    }
    return error.response?.data?.detail || error.message || defaultMessage;
  };

  // Fetch all roles
  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await services.roles.listRoles(0, 100, true);
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      showSnackbar(
        getErrorMessage(error, 'Failed to fetch roles.'),
        'error'
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Reset form data
  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      description: '',
      color: COLOR_OPTIONS[9].value,
      permissions: [],
      is_active: true,
    });
  };

  // Open create modal
  const handleOpenCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  // Open edit modal
  const handleOpenEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      label: role.label,
      description: role.description || '',
      color: role.color,
      permissions: role.permissions || [],
      is_active: role.is_active,
    });
    setShowEditModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle permission toggle
  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  // Create new role
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await services.roles.createRole(formData);
      showSnackbar('Role created successfully', 'success');
      setShowCreateModal(false);
      resetForm();
      fetchRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      showSnackbar(
        getErrorMessage(error, 'Failed to create role.'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // Update existing role
  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        label: formData.label,
        description: formData.description,
        color: formData.color,
        permissions: formData.permissions,
        is_active: formData.is_active,
      };
      await services.roles.updateRole(editingRole.id, updateData);
      showSnackbar('Role updated successfully', 'success');
      setShowEditModal(false);
      setEditingRole(null);
      resetForm();
      fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      showSnackbar(
        getErrorMessage(error, 'Failed to update role.'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete role
  const handleDelete = async (role) => {
    if (!window.confirm(`Are you sure you want to delete the "${role.label}" role?`)) {
      return;
    }
    try {
      await services.roles.deleteRole(role.id);
      showSnackbar('Role deleted successfully', 'success');
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      showSnackbar(
        getErrorMessage(error, 'Failed to delete role.'),
        'error'
      );
    }
  };

  // Seed default roles
  const handleSeedDefaults = async () => {
    try {
      await services.roles.seedDefaultRoles();
      showSnackbar('Default roles seeded successfully', 'success');
      fetchRoles();
    } catch (error) {
      console.error('Error seeding roles:', error);
      showSnackbar(
        getErrorMessage(error, 'Failed to seed default roles.'),
        'error'
      );
    }
  };

  // Role Form Modal extracted to top-level component to avoid remounts

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader label="Loading roles…" />
      </div>
    );
  }

  return (
    <div>
      {/* Header — stacks on phone: the heading + two buttons crushed each other
          at 375px. */}
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="ds-h2 text-fg-1">Define Roles</h2>
          <p className="text-sm text-fg-2">
            Create and manage custom roles for your organization
          </p>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <button
            type="button"
            onClick={handleSeedDefaults}
            className="btn btn-secondary flex-1 justify-center sm:flex-none"
          >
            Seed Defaults
          </button>
          <button
            type="button"
            onClick={handleOpenCreate}
            className="btn btn-primary flex-1 justify-center sm:flex-none"
          >
            + New Role
          </button>
        </div>
      </div>

      {/* Roles List — PHONE: one card per role. Everything visible, and Edit /
          Delete are real buttons instead of a column you scroll to. */}
      {isPhone ? (
        <div className="flex flex-col gap-3">
          {roles.length === 0 ? (
            <div className="rounded-xl border border-[var(--divider)] bg-bg-2 p-8 text-center text-sm text-fg-2">
              No roles defined. Use “Seed Defaults” to create default roles, or “New Role” for a custom one.
            </div>
          ) : (
            roles.map((role) => (
              <div key={role.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${role.color}`}>
                        {role.label}
                      </span>
                      {role.is_system && <span className="ds-badge role sm">System</span>}
                    </div>
                    <div className="mt-1 text-xs text-fg-2">{role.name}</div>
                  </div>
                  <span className={`ds-badge ${role.is_active ? 'profit' : 'loss'} sm shrink-0`}>
                    {role.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {role.description && (
                  <p className="mt-2 text-sm text-fg-1">{role.description}</p>
                )}

                <div className="mt-3 flex items-center justify-between gap-3 border-t border-[var(--divider)] pt-3">
                  <span className="text-sm text-fg-2">
                    {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
                  </span>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => handleOpenEdit(role)} className="btn btn-ghost btn-sm">
                      Edit
                    </button>
                    {!role.is_system && (
                      <button type="button" onClick={() => handleDelete(role)} className="btn btn-danger btn-sm">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
      <div className="bg-bg-2 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-bg-3 dark:bg-bg-3">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                Users
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-bg-2 divide-y divide-gray-200 dark:divide-gray-700">
            {roles.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-fg-2">
                  No roles defined. Click "Seed Defaults" to create default roles or "New Role" to create a custom role.
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id} className="hover:bg-bg-3">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${role.color}`}>
                        {role.label}
                      </span>
                      {role.is_system && (
                        <span className="ds-badge role sm">
                          System
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-fg-2 mt-1">
                      {role.name}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-fg-1 max-w-xs truncate">
                      {role.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-fg-1">
                      {role.user_count} {role.user_count === 1 ? 'user' : 'users'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`ds-badge ${role.is_active ? 'profit' : 'loss'} sm`}>
                      {role.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(role)}
                        className="btn btn-ghost btn-sm"
                      >
                        Edit
                      </button>
                      {!role.is_system && (
                        <button
                          type="button"
                          onClick={() => handleDelete(role)}
                          className="btn btn-danger btn-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Summary */}
      <div className="mt-4 text-sm text-fg-2">
        Total: {roles.length} roles
      </div>

      {/* Modals */}
      {showCreateModal && (
        <RoleFormModal
          isEdit={false}
          onSubmit={handleCreate}
          onClose={() => setShowCreateModal(false)}
          formData={formData}
          handleInputChange={handleInputChange}
          setFormData={setFormData}
          handlePermissionToggle={handlePermissionToggle}
          saving={saving}
        />
      )}
      {showEditModal && (
        <RoleFormModal
          isEdit={true}
          onSubmit={handleUpdate}
          onClose={() => {
            setShowEditModal(false);
            setEditingRole(null);
          }}
          formData={formData}
          handleInputChange={handleInputChange}
          setFormData={setFormData}
          handlePermissionToggle={handlePermissionToggle}
          saving={saving}
        />
      )}
    </div>
  );
};

export default RoleDefine;

// Extracted modal component to avoid losing focus due to remounts when parent re-renders
export const RoleFormModal = ({
  isEdit,
  onSubmit,
  onClose,
  formData,
  handleInputChange,
  setFormData,
  handlePermissionToggle,
  saving,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-bg-2 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ds-h2 text-fg-1">
            {isEdit ? 'Edit Role' : 'Create New Role'}
          </h2>
          <button
            onClick={onClose}
            className="text-fg-2 hover:text-fg-2 dark:text-fg-3 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Role Name (only for create) */}
          {!isEdit && (
            <div>
              <label htmlFor="role-name" className="block text-sm font-medium text-fg-2 mb-1">
                Role Name (ID)
              </label>
              <input
                id="role-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., custom_trader"
                pattern="^[a-z][a-z0-9_]*$"
                title="Lowercase letters, numbers, and underscores only. Must start with a letter."
                required
                autoFocus
                className="ds-input w-full"
              />
              <p className="text-xs text-fg-2 mt-1">
                Lowercase letters, numbers, and underscores only
              </p>
            </div>
          )}

          {/* Display Label */}
          <div>
            <label htmlFor="role-label" className="block text-sm font-medium text-fg-2 mb-1">
              Display Label
            </label>
            <input
              id="role-label"
              type="text"
              name="label"
              value={formData.label}
              onChange={handleInputChange}
              placeholder="e.g., Custom Trader"
              required
              className="ds-input w-full"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="role-description" className="block text-sm font-medium text-fg-2 mb-1">
              Description
            </label>
            <textarea
              id="role-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe what this role can do..."
              rows={3}
              className="ds-input w-full"
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Badge Color
            </label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {COLOR_OPTIONS.map((option) => (
                <button
                  key={option.name}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, color: option.value }))}
                  className={`p-2 rounded-lg border-2 ${
                    formData.color === option.value
                      ? 'border-blue-500'
                      : 'border-transparent'
                  }`}
                >
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.value}`}>
                    {option.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Permissions
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-[var(--divider)] rounded-lg">
              {AVAILABLE_PERMISSIONS.map((perm) => (
                <label
                  key={perm.id}
                  className="flex items-start gap-2 p-2 rounded hover:bg-bg-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(perm.id)}
                    onChange={() => handlePermissionToggle(perm.id)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-fg-1">{perm.label}</div>
                    <div className="text-xs text-fg-2">{perm.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <input
              id="role-active"
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              className="rounded"
            />
            <label htmlFor="role-active" className="text-sm text-fg-2">
              Role is active
            </label>
          </div>

          {/* Preview */}
          <div className="p-4 bg-bg-3 dark:bg-bg-3 rounded-lg">
            <div className="text-sm text-fg-2 mb-2">Preview:</div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${formData.color}`}>
              {formData.label || 'Role Label'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[var(--divider)]">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update Role' : 'Create Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);
